// src/components/ThumbnailGenerator/hooks/useThumbnailEditor.ts
import { useState, useCallback, useRef } from 'react';
import { 
  ImageAdjustments, 
  TextElement, 
  ShapeElement, 
  AssetElement,
  ThumbnailComposition 
} from '../../../types/thumbnailTypes';
import { composeThumbnail, uploadComposedThumbnail } from '../../../services/thumbnailCompositionApi';
import { projectService } from '../../../services/projectService';
import { getApiUrl } from '../../../config/api';

export interface ThumbnailEditorState {
  baseImage: string;
  originalBaseImage: string; // Keep original for editing
  imageAdjustments: ImageAdjustments;
  textElements: TextElement[];
  shapeElements: ShapeElement[];
  assetElements: AssetElement[];
  selectedElement: string | null;
  canvasSettings: {
    backgroundColor?: string;
    backgroundOpacity?: number;
  };
  isLoading: boolean;
  isGenerating: boolean;
  history: ThumbnailEditorState[];
  historyIndex: number;
}

const initialState: ThumbnailEditorState = {
  baseImage: '',
  originalBaseImage: '', // Store the original image for editing
  imageAdjustments: {
    contrast: 0,
    brightness: 0,
    blackAndWhite: false,
    position: { x: 0, y: 0 },
    scale: { scaleX: 1, scaleY: 1 },
    rotation: 0
  },
  textElements: [],
  shapeElements: [],
  assetElements: [],
  selectedElement: null,
  canvasSettings: {
    backgroundColor: '#000000',
    backgroundOpacity: 100
  },
  isLoading: false,
  isGenerating: false,
  history: [],
  historyIndex: -1
};

export const useThumbnailEditor = () => {
  const [state, setState] = useState<ThumbnailEditorState>(initialState);
  
  // Generate unique IDs with timestamp and random component to prevent collisions
  const generateUniqueId = (prefix: string) => {
    const id = `${prefix}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    console.log(`Generated unique ID: ${id}`);
    return id;
  };

  const saveToHistory = useCallback((newState: Partial<ThumbnailEditorState>) => {
    setState(prevState => {
      const updatedState = { ...prevState, ...newState };
      const newHistory = prevState.history.slice(0, prevState.historyIndex + 1);
      newHistory.push({ ...updatedState });
      
      return {
        ...updatedState,
        history: newHistory.slice(-50), // Keep last 50 states
        historyIndex: Math.min(newHistory.length - 1, 49)
      };
    });
  }, []);

  const updateState = useCallback((updates: Partial<ThumbnailEditorState>, saveHistory = true) => {
    if (saveHistory) {
      saveToHistory(updates);
    } else {
      setState(prev => ({ ...prev, ...updates }));
    }
  }, [saveToHistory]);

  // Helper function to upload existing image URL as thumbnail
  const uploadExistingImageAsThumbnail = async (projectId: string, imageUrl: string) => {
    try {
      // Fetch the image from the URL
      const response = await fetch(imageUrl);
      if (!response.ok) {
        throw new Error(`Failed to fetch image: ${response.statusText}`);
      }
      
      const blob = await response.blob();
      
      // Create a File object from the blob
      const filename = `thumbnail_${Date.now()}.png`;
      const file = new File([blob], filename, { type: blob.type || 'image/png' });
      
      // Create FormData like the upload process
      const formData = new FormData();
      formData.append('method', 'upload');
      formData.append('image', file);
      formData.append('projectId', projectId);
      formData.append('title', 'Selected Thumbnail');
      formData.append('width', '1536');
      formData.append('height', '864');
      
      // Get auth headers
      const token = localStorage.getItem('authToken');
      const headers: Record<string, string> = {};
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      // Upload to the same endpoint as file uploads
      const uploadResponse = await fetch(getApiUrl('/thumbnail/generate'), {
        method: 'POST',
        headers,
        body: formData
      });
      
      if (!uploadResponse.ok) {
        throw new Error(`Upload failed: ${uploadResponse.statusText}`);
      }
      
      const result = await uploadResponse.json();
      console.log('📤 Upload result:', result);
      return result;
      
    } catch (error) {
      console.error('❌ Error uploading existing image:', error);
      throw error;
    }
  };

  const loadProject = useCallback(async (projectId: string, baseImageUrl?: string) => {
    updateState({ isLoading: true }, false);
    try {
      const project = await projectService.getProject(projectId);
      
      // Priority 1: Use provided baseImageUrl (from URL params after generation)
      if (baseImageUrl) {
        console.log('🔄 About to save base image to project:', { projectId, baseImageUrl });
        
        // Upload the image using the same API as file uploads
        try {
          console.log('🚀 Uploading existing image as thumbnail...');
          await uploadExistingImageAsThumbnail(projectId, baseImageUrl);
          console.log('✅ Successfully uploaded existing image as thumbnail');
        } catch (uploadError) {
          console.warn('⚠️ Failed to upload image as thumbnail:', uploadError);
          // Continue even if upload fails - user can still edit
        }
        
        updateState({
          baseImage: baseImageUrl,
          originalBaseImage: baseImageUrl, // Store original for composition API
          isLoading: false
        }, false);
      }
      // Priority 2: Load the base thumbnail image if it exists in project
      else if (project.thumbnail) {
        // Always use the original base image for editing, not the composed one
        // The thumbnail field should contain the original AI-generated image
        updateState({
          baseImage: project.thumbnail,
          originalBaseImage: project.thumbnail, // Store original for composition API
          isLoading: false
        }, false);
      } else {
        // No base image yet - user needs to generate one first
        updateState({ isLoading: false }, false);
      }
    } catch (error) {
      console.error('Failed to load project:', error);
      updateState({ isLoading: false }, false);
    }
  }, [updateState]);

  // Image adjustment functions
  const updateImageAdjustments = useCallback((adjustments: Partial<ImageAdjustments>) => {
    updateState({
      imageAdjustments: { ...state.imageAdjustments, ...adjustments }
    });
  }, [state.imageAdjustments, updateState]);  // Text element functions
  const addTextElement = useCallback((text: string, position: { x: number; y: number }, customStyle?: Partial<TextElement['style']>) => {
    const defaultStyle = {
      fontSize: 48,
      fontFamily: 'Bebas Neue',
      color: '#FFFFFF',
      textAlign: 'center' as const,
      shadow: {
        enabled: true,
        color: '#000000',
        offsetX: 2,
        offsetY: 2,
        blur: 4
      },
      border: {
        enabled: false,
        size: 2,
        color: '#000000'
      },
      background: {
        enabled: false,
        color: '#000000',
        opacity: 100,
        padding: 8
      }
    };

    const newElement: TextElement = {
      id: generateUniqueId('text'),
      text,
      position,
      style: { ...defaultStyle, ...customStyle }
    };

    updateState({
      textElements: [...state.textElements, newElement],
      selectedElement: newElement.id
    });
  }, [state.textElements, updateState]);

  const updateTextElement = useCallback((id: string, updates: Partial<TextElement>) => {
    updateState({
      textElements: state.textElements.map(el => 
        el.id === id ? { ...el, ...updates } : el
      )
    });
  }, [state.textElements, updateState]);

  const removeTextElement = useCallback((id: string) => {
    updateState({
      textElements: state.textElements.filter(el => el.id !== id),
      selectedElement: state.selectedElement === id ? null : state.selectedElement
    });
  }, [state.textElements, state.selectedElement, updateState]);

  // Shape element functions
  const addShapeElement = useCallback((type: 'rectangle' | 'circle', position: { x: number; y: number }) => {
    const newElement: ShapeElement = {
      id: generateUniqueId('shape'),
      type,
      position,
      dimensions: { width: 200, height: 100 },
      style: {
        fillColor: '#3b82f6',
        fillOpacity: 100,
        borderColor: '#ffffff',
        borderWidth: 0
      },
      rotation: 0
    };

    updateState({
      shapeElements: [...state.shapeElements, newElement],
      selectedElement: newElement.id
    });
  }, [state.shapeElements, updateState]);

  const updateShapeElement = useCallback((id: string, updates: Partial<ShapeElement>) => {
    updateState({
      shapeElements: state.shapeElements.map(el => 
        el.id === id ? { ...el, ...updates } : el
      )
    });
  }, [state.shapeElements, updateState]);

  const removeShapeElement = useCallback((id: string) => {
    updateState({
      shapeElements: state.shapeElements.filter(el => el.id !== id),
      selectedElement: state.selectedElement === id ? null : state.selectedElement
    });
  }, [state.shapeElements, state.selectedElement, updateState]);

  // Asset element functions
  const addAssetElement = useCallback((asset: AssetElement) => {
    updateState({
      assetElements: [...state.assetElements, asset],
      selectedElement: asset.id
    });
  }, [state.assetElements, updateState]);

  const updateAssetElement = useCallback((id: string, updates: Partial<AssetElement>) => {
    updateState({
      assetElements: state.assetElements.map(el => 
        el.id === id ? { ...el, ...updates } : el
      )
    });
  }, [state.assetElements, updateState]);

  const removeAssetElement = useCallback((id: string) => {
    console.log(`🗑️ Removing asset element: ${id}`);
    console.log(`Current asset elements count: ${state.assetElements.length}`);
    
    const newAssetElements = state.assetElements.filter(el => el.id !== id);
    console.log(`New asset elements count: ${newAssetElements.length}`);
    
    updateState({
      assetElements: newAssetElements,
      selectedElement: state.selectedElement === id ? null : state.selectedElement
    });
  }, [state.assetElements, state.selectedElement, updateState]);  // Selection and history functions
  const selectElement = useCallback((elementId: string | null) => {
    updateState({ selectedElement: elementId }, false);
  }, [updateState]);

  const undo = useCallback(() => {
    if (state.historyIndex > 0) {
      const previousState = state.history[state.historyIndex - 1];
      setState(prev => ({
        ...previousState,
        history: prev.history,
        historyIndex: prev.historyIndex - 1
      }));
    }
  }, [state.historyIndex, state.history]);

  const redo = useCallback(() => {
    if (state.historyIndex < state.history.length - 1) {
      const nextState = state.history[state.historyIndex + 1];
      setState(prev => ({
        ...nextState,
        history: prev.history,
        historyIndex: prev.historyIndex + 1
      }));
    }
  }, [state.historyIndex, state.history]);

  // Composition and export functions
  const generateFinalThumbnail = useCallback(async (projectId: string, canvasSize: { width: number; height: number }, imageSize: { width: number; height: number }) => {
    // Use originalBaseImage for composition, not the current baseImage
    if (!state.originalBaseImage && !state.baseImage) {
      throw new Error('No base image available for composition');
    }

    updateState({ isGenerating: true }, false);
    
    try {
      // Scale coordinates from canvas size to actual image size
      const scaleX = imageSize.width / canvasSize.width;
      const scaleY = imageSize.height / canvasSize.height;
      
      // Scale text element positions and font sizes
      const scaledTextElements = state.textElements.map(element => {
        // Calculate dynamic font scaling based on scale factor
        // Larger scale factors need more reduction to match browser rendering
        const fontScaleFactor = scaleX > 3 ? 0.6 : scaleX > 2 ? 0.7 : 0.8;
        const scaledFontSize = Math.round(element.style.fontSize * scaleX * fontScaleFactor);
        
        // Calculate dynamic position offset based on scale and canvas size
        // Larger images need proportionally larger offsets
        const positionOffsetX = Math.round(scaleX * -50); // Dynamic offset based on scale
        
        return {
          ...element,
          position: {
            x: Math.round(element.position.x * scaleX + positionOffsetX),
            y: Math.round(element.position.y * scaleY)
          },
          style: {
            ...element.style,
            // Cap font size to backend limits (8-200px)
            fontSize: Math.max(8, Math.min(200, scaledFontSize))
          }
        };
      });
      
      // Scale shape element positions and dimensions
      const scaledShapeElements = state.shapeElements.map(element => ({
        ...element,
        position: {
          x: Math.round(element.position.x * scaleX),
          y: Math.round(element.position.y * scaleY)
        },
        dimensions: {
          width: Math.round(element.dimensions.width * scaleX),
          height: Math.round(element.dimensions.height * scaleY)
        }
      }));
      
      // Scale asset element positions and dimensions
      const scaledAssetElements = state.assetElements.map(element => ({
        ...element,
        position: {
          x: Math.round(element.position.x * scaleX),
          y: Math.round(element.position.y * scaleY)
        },
        dimensions: {
          width: Math.round(element.dimensions.width * scaleX),
          height: Math.round(element.dimensions.height * scaleY)
        }
      }));

      const composition: ThumbnailComposition = {
        projectId,
        // Always use the original base image for composition
        baseImageUrl: state.originalBaseImage || state.baseImage,
        imageAdjustments: state.imageAdjustments,
        textElements: scaledTextElements,
        shapeElements: scaledShapeElements,
        assetElements: scaledAssetElements,
        canvasSettings: state.canvasSettings
      };

      // Debug logging to see what we're sending
      console.log('Composition data being sent:', {
        canvasSize,
        imageSize,
        scaleX,
        scaleY,
        originalTextElements: state.textElements,
        scaledTextElements,
        composition
      });

      // Call the backend composition API
      const result = await composeThumbnail(composition);
      
      // DON'T update the baseImage with the composed result
      // Keep the original for continued editing
      updateState({ 
        isGenerating: false
        // baseImage stays as original for editing
      }, false);
      
      return {
        ...result,
        downloadUrl: result.s3Url // This is the final composed image for download
      };
    } catch (error) {
      updateState({ isGenerating: false }, false);
      throw error;
    }
  }, [state, updateState]);

  // Helper function to load image with authentication using proxy
  const loadImageWithAuth = async (imageUrl: string): Promise<HTMLImageElement> => {
    console.log('🖼️ [LOAD_IMAGE] Loading image:', imageUrl);
    
    // For S3 URLs, use the backend proxy to avoid CORS issues
    if (imageUrl.includes('s3.amazonaws.com') || imageUrl.includes('aiapptest2.s3')) {
      console.log('🔗 [LOAD_IMAGE] Using proxy for S3 image');
      
      // Use the same proxy pattern as audio files with authentication
      const proxiedUrl = `${getApiUrl('')}/api/proxy-image?url=${encodeURIComponent(imageUrl)}`;
      console.log('🖼️ [LOAD_IMAGE] Proxied URL:', proxiedUrl);
      
      // Get auth token
      const token = localStorage.getItem('authToken');
      
      if (token) {
        try {
          console.log('🔐 Fetching proxied image with authentication...');
          
          // Fetch with authentication headers (same pattern as audio proxy)
          const response = await fetch(proxiedUrl, {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            mode: 'cors',
            credentials: 'omit'
          });
          
          console.log('📡 Proxied image fetch response:', {
            status: response.status,
            statusText: response.statusText,
            contentType: response.headers.get('content-type')
          });
          
          if (response.ok) {
            // Convert to blob and create object URL (same as audio proxy)
            const imageBlob = await response.blob();
            const blobUrl = URL.createObjectURL(imageBlob);
            
            return new Promise<HTMLImageElement>((resolve, reject) => {
              const img = new Image();
              img.onload = () => {
                console.log('✅ [LOAD_IMAGE] Successfully loaded proxied image via blob');
                // Clean up the blob URL after loading
                URL.revokeObjectURL(blobUrl);
                resolve(img);
              };
              img.onerror = (error) => {
                console.error('❌ [LOAD_IMAGE] Failed to load image blob:', error);
                URL.revokeObjectURL(blobUrl);
                reject(error);
              };
              img.src = blobUrl;
            });
          } else {
            const errorText = await response.text();
            console.error('❌ Failed to fetch proxied image:', response.status, response.statusText, errorText);
            throw new Error(`Proxy failed: ${response.status} ${response.statusText}`);
          }
        } catch (fetchError) {
          console.error('❌ Error fetching proxied image:', fetchError);
          // Fall through to try original URL
        }
      } else {
        console.warn('⚠️ No auth token available for proxied image request');
      }
      
      // Fallback: try original URL without proxy
      console.log('🔄 Falling back to original S3 URL');
      return new Promise<HTMLImageElement>((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => {
          console.log('✅ [LOAD_IMAGE] Successfully loaded original S3 image');
          resolve(img);
        };
        img.onerror = (error) => {
          console.error('❌ [LOAD_IMAGE] Failed to load original S3 image:', error);
          reject(error);
        };
        img.src = imageUrl;
      });
    }
    
    // For other URLs that might need authentication, use fetch with auth headers
    const token = localStorage.getItem('authToken');
    const headers: Record<string, string> = {};
    
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    
    try {
      const response = await fetch(imageUrl, { headers });
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      
      return new Promise<HTMLImageElement>((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => {
          URL.revokeObjectURL(blobUrl);
          resolve(img);
        };
        img.onerror = (error) => {
          URL.revokeObjectURL(blobUrl);
          reject(error);
        };
        img.src = blobUrl;
      });
    } catch (error) {
      // Fallback to direct image loading
      return new Promise<HTMLImageElement>((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => resolve(img);
        img.onerror = reject;
        img.src = imageUrl;
      });
    }
  };

  // Helper function to apply CSS filters to canvas
  const applyFiltersToCanvas = (
    sourceCanvas: HTMLCanvasElement, 
    filters: string
  ): HTMLCanvasElement => {
    const filteredCanvas = document.createElement('canvas');
    const filteredCtx = filteredCanvas.getContext('2d');
    if (!filteredCtx) throw new Error('Could not get filtered canvas context');
    
    // Set same dimensions as source
    filteredCanvas.width = sourceCanvas.width;
    filteredCanvas.height = sourceCanvas.height;
    
    console.log(`Applying CSS filters: "${filters}" to ${sourceCanvas.width}x${sourceCanvas.height} canvas`);
    
    // Apply CSS filters and draw the source canvas
    filteredCtx.filter = filters;
    filteredCtx.drawImage(sourceCanvas, 0, 0);
    
    // Reset filter to prevent affecting other operations
    filteredCtx.filter = 'none';
    
    console.log('CSS filters applied successfully to canvas');
    return filteredCanvas;
  };

  // Helper function to build CSS filter string (same logic as ThumbnailCanvas)
  const buildFilterString = (imageAdjustments: ImageAdjustments): string => {
    const filters = [];
    
    if (imageAdjustments.contrast !== 0) {
      filters.push(`contrast(${100 + imageAdjustments.contrast}%)`);
    }
    if (imageAdjustments.brightness !== 0) {
      filters.push(`brightness(${100 + imageAdjustments.brightness}%)`);
    }
    if (imageAdjustments.blackAndWhite) {
      filters.push('grayscale(100%)');
    }
    
    const filterString = filters.length > 0 ? filters.join(' ') : 'none';
    console.log('Built filter string:', filterString, 'from adjustments:', imageAdjustments);
    
    return filterString;
  };

  // Frontend canvas-based composition (using backend proxy)
  const generateFinalThumbnailCanvas = useCallback(async (projectId: string, editorCanvasDimensions?: { width: number; height: number }, targetResolution?: { width: number; height: number }) => {
    if (!state.baseImage) {
      throw new Error('No base image available');
    }

    updateState({ isGenerating: true }, false);
    
    // Small delay to ensure state has fully updated
    await new Promise(resolve => setTimeout(resolve, 100));
    
    try {
      console.log('🎨 Starting canvas generation for baseImage:', state.baseImage);
      console.log(`📊 Current state - Assets: ${state.assetElements.length}, Text: ${state.textElements.length}, Shapes: ${state.shapeElements.length}`);
      console.log('🔍 Asset elements details:', state.assetElements.map(a => ({ id: a.id, url: a.assetUrl })));
      
      // Load base image with authentication
      const img = await loadImageWithAuth(state.baseImage);
      console.log('Base image loaded successfully');
      
      // Create canvas with the loaded image
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      if (!ctx) throw new Error('Could not get canvas context');
      
      console.log('Canvas and context created successfully');
      
      return new Promise<{ projectId: string; downloadUrl: string }>((resolve, reject) => {
        // Add timeout to prevent infinite hanging
        const timeoutId = setTimeout(() => {
          console.error('Thumbnail generation timed out after 30 seconds');
          updateState({ isGenerating: false }, false);
          reject(new Error('Thumbnail generation timed out'));
        }, 30000); // 30 second timeout
        const processImage = async () => {
          console.log('Image loaded successfully, dimensions:', img.naturalWidth, 'x', img.naturalHeight);
          try {
            // Set canvas dimensions to target resolution instead of source image dimensions
            canvas.width = targetResolution?.width || img.naturalWidth;
            canvas.height = targetResolution?.height || img.naturalHeight;
            
            console.log(`Target resolution: ${canvas.width}x${canvas.height} (was ${img.naturalWidth}x${img.naturalHeight})`);
            
            // Calculate scaling factor between editor display and target output
            // Use the dimensions passed from the ThumbnailCanvas component, with fallbacks
            const editorCanvasWidth = editorCanvasDimensions?.width || 640;
            const editorCanvasHeight = editorCanvasDimensions?.height || 360;
            
            // Use consistent scale factors for both X and Y to maintain exact positioning
            const scaleFactorX = canvas.width / editorCanvasWidth;
            const scaleFactorY = canvas.height / editorCanvasHeight;
            
            // Use the same scale factors for positions to maintain exact positioning
            const positionScaleX = scaleFactorX;
            const positionScaleY = scaleFactorY;
            
            // Use the average scale factor for font sizes to maintain proportions
            const fontScaleFactor = (scaleFactorX + scaleFactorY) / 2;
            
            console.log(`Canvas: ${canvas.width}x${canvas.height}, Editor: ${editorCanvasWidth}x${editorCanvasHeight}, Scale Factors: X=${scaleFactorX.toFixed(2)}, Y=${scaleFactorY.toFixed(2)}, Font Scale: ${fontScaleFactor.toFixed(2)}`);
            
            console.log('Starting image adjustments and base image drawing...');
            // Apply image adjustments and draw base image
            ctx.save();
            
            // Apply image scaling if provided
            if (state.imageAdjustments.scale) {
              let scaleX = state.imageAdjustments.scale.scaleX || 1;
              let scaleY = state.imageAdjustments.scale.scaleY || 1;
              
              // Auto-reduce scale during generation to prevent clipping
              // Only reduce if user has scaled UP (> 1.0)
              const scaleReductionFactor = 0.6; // Keep 60% of zoom amount (40% reduction)
              if (scaleX > 1.0) {
                scaleX = 1 + ((scaleX - 1) * scaleReductionFactor);
              }
              if (scaleY > 1.0) {
                scaleY = 1 + ((scaleY - 1) * scaleReductionFactor);
              }
              
              console.log('SCALE VALUES:', { 
                original: { 
                  scaleX: state.imageAdjustments.scale.scaleX, 
                  scaleY: state.imageAdjustments.scale.scaleY 
                },
                adjusted: { scaleX, scaleY },
                reduction: scaleReductionFactor
              });
              
              // Scale from center
              ctx.translate(canvas.width / 2, canvas.height / 2);
              ctx.scale(scaleX, scaleY);
              ctx.translate(-canvas.width / 2, -canvas.height / 2);
            }
            
            // Apply position offset if provided
            if (state.imageAdjustments.position) {
              ctx.translate(state.imageAdjustments.position.x || 0, state.imageAdjustments.position.y || 0);
            }
            
            // Draw the base image (respecting original aspect ratio with scaling)
            // Don't force the image to fill canvas - let it maintain its scaled proportions
            const imageAspectRatio = img.naturalWidth / img.naturalHeight;
            const canvasAspectRatio = canvas.width / canvas.height;
            
            let drawWidth, drawHeight, drawX, drawY;
            
            if (imageAspectRatio > canvasAspectRatio) {
              // Image is wider than canvas - fit to width
              drawWidth = canvas.width;
              drawHeight = canvas.width / imageAspectRatio;
              drawX = 0;
              drawY = (canvas.height - drawHeight) / 2;
            } else {
              // Image is taller than canvas - fit to height
              drawHeight = canvas.height;
              drawWidth = canvas.height * imageAspectRatio;
              drawX = (canvas.width - drawWidth) / 2;
              drawY = 0;
            }
            
            ctx.drawImage(img, drawX, drawY, drawWidth, drawHeight);
            
            // Apply CSS filters to the canvas if any adjustments are needed
            const filterString = buildFilterString(state.imageAdjustments);
            if (filterString !== 'none') {
              console.log('Applying filters to canvas:', filterString);
              
              // Create a temporary canvas with the current content
              const tempCanvas = document.createElement('canvas');
              const tempCtx = tempCanvas.getContext('2d');
              if (!tempCtx) throw new Error('Could not get temporary canvas context');
              
              tempCanvas.width = canvas.width;
              tempCanvas.height = canvas.height;
              
              // Copy current canvas content to temp canvas
              tempCtx.drawImage(canvas, 0, 0);
              
              // Apply filters to temp canvas and draw back to main canvas
              const filteredCanvas = applyFiltersToCanvas(tempCanvas, filterString);
              
              // Clear main canvas and draw filtered result
              ctx.clearRect(0, 0, canvas.width, canvas.height);
              ctx.drawImage(filteredCanvas, 0, 0);
              
              console.log('Filters applied successfully to canvas');
            } else {
              console.log('No filters to apply - using original image');
            }
            
            ctx.restore();
            
            
            console.log('Base image drawn, starting shape elements...');
            // Draw shape elements FIRST (as backgrounds behind text)
            state.shapeElements.forEach((shapeElement, index) => {
              console.log(`Drawing shape ${index + 1}/${state.shapeElements.length}:`, shapeElement.type);
              ctx.save();
              
              // Scale shape properties using consistent scaling without arbitrary adjustments
              const scaledWidth = Math.round(shapeElement.dimensions.width * scaleFactorX);
              const scaledHeight = Math.round(shapeElement.dimensions.height * scaleFactorY);
              
              // Scale positions exactly as they appear in the editor
              const scaledX = Math.round(shapeElement.position.x * positionScaleX);
              const scaledY = Math.round(shapeElement.position.y * positionScaleY);
              
              console.log(`Shape ${index + 1} position: x=${scaledX}, y=${scaledY}, size=${scaledWidth}x${scaledHeight}`);
              
              ctx.fillStyle = shapeElement.style.fillColor;
              ctx.globalAlpha = (shapeElement.style.fillOpacity || 100) / 100;
              
              if (shapeElement.type === 'rectangle') {
                ctx.fillRect(scaledX, scaledY, scaledWidth, scaledHeight);
              } else if (shapeElement.type === 'circle') {
                ctx.beginPath();
                // Use ellipse to support different width and height (oval shapes)
                const radiusX = scaledWidth / 2;
                const radiusY = scaledHeight / 2;
                const centerX = scaledX + radiusX;
                const centerY = scaledY + radiusY;
                
                console.log(`Drawing ellipse: center=(${centerX}, ${centerY}), radiusX=${radiusX}, radiusY=${radiusY}`);
                
                // Use ellipse method for proper oval support
                ctx.ellipse(centerX, centerY, radiusX, radiusY, 0, 0, 2 * Math.PI);
                ctx.fill();
              }
              
              ctx.restore();
            });
            
            console.log('Shape elements completed, starting asset elements...');
            console.log(`Total asset elements to draw: ${state.assetElements.length}`);
            
            // Double-check we have the latest state
            if (state.assetElements.length === 0) {
              console.log('⚠️ No asset elements to draw - skipping asset rendering');
            } else {
              console.log('📋 Asset elements to process:');
              state.assetElements.forEach((asset, i) => {
                console.log(`  Asset ${i + 1}: ID=${asset.id}, URL=${asset.assetUrl.substring(asset.assetUrl.lastIndexOf('/') + 1)}, pos=(${asset.position.x}, ${asset.position.y}), size=${asset.dimensions.width}x${asset.dimensions.height}, opacity=${asset.opacity}%`);
              });
            }
            // Draw asset elements SECOND (on top of shapes, but behind text)
            const assetPromises = state.assetElements.map(async (assetElement, index) => {
              return new Promise<void>(async (resolve, reject) => {
                console.log(`Drawing asset ${index + 1}/${state.assetElements.length}:`, assetElement.assetUrl);
                
                // Load asset image with authentication
                try {
                  console.log(`Loading asset image: ${assetElement.assetUrl}`);
                  
                  // Use signed URL proxy for S3 assets to avoid CORS issues
                  let imageUrlToLoad = assetElement.assetUrl;
                  if (assetElement.assetUrl.includes('s3.amazonaws.com') || assetElement.assetUrl.includes('amazonaws.com')) {
                    console.log('🔗 Using signed URL proxy for S3 asset');
                    try {
                      const token = localStorage.getItem('authToken');
                      const headers: Record<string, string> = {
                        'Content-Type': 'application/json'
                      };
                      if (token) {
                        headers['Authorization'] = `Bearer ${token}`;
                      }
                      
                      const proxyResponse = await fetch(getApiUrl('/video/get-signed-download-url'), {
                        method: 'POST',
                        headers,
                        body: JSON.stringify({ videoUrl: assetElement.assetUrl })
                      });
                      
                      if (proxyResponse.ok) {
                        const proxyData = await proxyResponse.json();
                        if (proxyData.signedUrl) {
                          imageUrlToLoad = proxyData.signedUrl;
                          console.log('✅ Got signed URL for asset');
                        } else {
                          console.warn('⚠️ No signed URL in response, using original URL');
                        }
                      } else {
                        const errorText = await proxyResponse.text();
                        console.warn('⚠️ Failed to get signed URL:', proxyResponse.status, errorText);
                      }
                    } catch (proxyError) {
                      console.warn('⚠️ Signed URL proxy failed, using original URL:', proxyError);
                    }
                  }
                  
                  const assetImg = await loadImageWithAuth(imageUrlToLoad);
                  console.log(`Asset image loaded successfully: ${assetImg.width}x${assetImg.height}`);
                  
                  ctx.save();
                  
                  // Scale asset properties
                  const scaledWidth = Math.round(assetElement.dimensions.width * scaleFactorX);
                  const scaledHeight = Math.round(assetElement.dimensions.height * scaleFactorY);
                  const scaledX = Math.round(assetElement.position.x * positionScaleX);
                  const scaledY = Math.round(assetElement.position.y * positionScaleY);
                  
                  console.log(`Asset ${index + 1} position: x=${scaledX}, y=${scaledY}, size=${scaledWidth}x${scaledHeight}`);
                  
                  // Apply opacity
                  ctx.globalAlpha = (assetElement.opacity || 100) / 100;
                  
                  // Apply rotation if specified
                  if (assetElement.rotation && assetElement.rotation !== 0) {
                    const centerX = scaledX + scaledWidth / 2;
                    const centerY = scaledY + scaledHeight / 2;
                    ctx.translate(centerX, centerY);
                    ctx.rotate((assetElement.rotation * Math.PI) / 180);
                    ctx.translate(-centerX, -centerY);
                  }
                  
                  // Draw the asset image
                  console.log(`Drawing asset at scaled position: (${scaledX}, ${scaledY}) with size ${scaledWidth}x${scaledHeight}`);
                  ctx.drawImage(assetImg, scaledX, scaledY, scaledWidth, scaledHeight);
                  console.log(`Asset ${index + 1} drawn successfully`);
                  
                  ctx.restore();
                  resolve();
                } catch (error) {
                  console.error(`❌ Failed to load asset image: ${assetElement.assetUrl}`, error);
                  console.error('Asset element details:', assetElement);
                  resolve(); // Continue even if asset fails to load
                }
              });
            });
            
            // Wait for all assets to load before proceeding to text
            await Promise.all(assetPromises);
            console.log(`✅ Asset elements completed successfully (${state.assetElements.length} assets processed)`);
            
            console.log('Asset elements completed, starting text elements...');
            // Draw text elements SECOND (on top of shapes)
            state.textElements.forEach((textElement, index) => {
              console.log(`Drawing text ${index + 1}/${state.textElements.length}:`, textElement.text);
              console.log('Text element full data:', JSON.stringify(textElement, null, 2));
              ctx.save();
              
              // Scale text properties using consistent font scaling
              const scaledFontSize = Math.round(textElement.style.fontSize * fontScaleFactor);
              
              // Scale positions exactly as they appear in the editor
              const scaledX = Math.round(textElement.position.x * positionScaleX);
              const scaledY = Math.round(textElement.position.y * positionScaleY + 15);
              
              console.log(`Text ${index + 1} "${textElement.text}" position: x=${scaledX}, y=${scaledY}, fontSize=${scaledFontSize}`);
              
              // Set font properties with consistent scaling
              ctx.font = `${scaledFontSize}px ${textElement.style.fontFamily}`;
              ctx.fillStyle = textElement.style.color;
              ctx.textAlign = textElement.style.textAlign || 'left'; // Use element's textAlign property
              ctx.textBaseline = 'top'; // Revert to top to match CSS behavior
              
              // Draw text border FIRST to match WebkitTextStroke behavior
              // Check if border is enabled - ONLY draw if explicitly enabled
              const hasBorder = textElement.style.border?.enabled === true;
                               
              console.log(`Border check for "${textElement.text}":`, {
                enabled: textElement.style.border?.enabled,
                size: textElement.style.border?.size,
                color: textElement.style.border?.color,
                hasBorder
              });
              
              if (hasBorder) {
                // Save current context
                ctx.save();
                
                // Clear any shadow for border
                ctx.shadowColor = 'transparent';
                // ctx.strokeStyle = textElement.style.border?.color || '#FFFFFF';
                // // Use much thinner border - closer to WebkitTextStroke thickness
                // ctx.lineWidth = Math.round((textElement.style.border?.size || 1) * fontScaleFactor * 0.5);
                // ctx.lineJoin = 'round';
                // ctx.lineCap = 'round';
                ctx.strokeStyle = textElement.style.border.color || '#FFFFFF';
                ctx.lineWidth = Math.round((textElement.style.border.size || 2) * fontScaleFactor *2); // Double the border width
                console.log(`Drawing border: color=${ctx.strokeStyle}, width=${ctx.lineWidth}, enabled=${textElement.style.border.enabled}`);
                
                console.log(`Drawing border: color=${ctx.strokeStyle}, width=${ctx.lineWidth}`);
                
                // Draw stroke fewer times for thinner appearance
                ctx.strokeText(textElement.text, scaledX, scaledY);
                
                // Restore context
                ctx.restore();
              } else {
                console.log(`No border detected for "${textElement.text}"`);
              }
              
              // Apply shadow for main text only
              if (textElement.style.shadow?.enabled) {
                ctx.shadowColor = textElement.style.shadow.color || '#000000';
                ctx.shadowOffsetX = Math.round((textElement.style.shadow.offsetX || 2) * fontScaleFactor);
                ctx.shadowOffsetY = Math.round((textElement.style.shadow.offsetY || 2) * fontScaleFactor);
                ctx.shadowBlur = Math.round((textElement.style.shadow.blur || 4) * fontScaleFactor);
                console.log(`Applied shadow: ${ctx.shadowColor}, offset: ${ctx.shadowOffsetX}x${ctx.shadowOffsetY}, blur: ${ctx.shadowBlur}`);
              }
              
              // Draw main text with shadow
              ctx.fillText(textElement.text, scaledX, scaledY);
              
              // Clear shadow for next element
              ctx.shadowColor = 'transparent';
              
              ctx.restore();
            });
            
            console.log('Text elements completed, converting to blob and uploading...');
            
            // Convert canvas to blob instead of data URL
            canvas.toBlob(async (blob) => {
              if (!blob) {
                throw new Error('Failed to create blob from canvas');
              }
              
              try {
                console.log('Canvas blob created, size:', blob.size);
                
                // Create composition data to send to API
                const composition: ThumbnailComposition = {
                  projectId,
                  baseImageUrl: state.baseImage,
                  imageAdjustments: state.imageAdjustments,
                  textElements: state.textElements,
                  shapeElements: state.shapeElements,
                  assetElements: state.assetElements,
                  canvasSettings: {
                    backgroundColor: '#000000',
                    backgroundOpacity: 100
                  }
                };
                
                console.log('Uploading composed thumbnail to S3 via API...');
                
                // Use the new uploadComposedThumbnail API function
                const result = await uploadComposedThumbnail(projectId, blob, composition);
                
                console.log('✅ Thumbnail uploaded to S3:', result.s3Url);
                
                // Update the project's thumbnail field in the database
                try {
                  const updateResponse = await fetch(getApiUrl(`/projects/${projectId}`), {
                    method: 'PATCH',
                    headers: {
                      'Content-Type': 'application/json',
                      'Authorization': `Bearer ${localStorage.getItem('authToken')}`
                    },
                    body: JSON.stringify({
                      thumbnail: result.s3Url
                    })
                  });
                  
                  if (updateResponse.ok) {
                    console.log('✅ Project thumbnail field updated in database');
                    
                    // Update the local state with the new S3 URL
                    updateState({ 
                      baseImage: result.s3Url 
                    }, false);
                  } else {
                    console.warn('⚠️ Failed to update project thumbnail field:', updateResponse.statusText);
                  }
                } catch (dbError) {
                  console.warn('⚠️ Database update failed but S3 upload succeeded:', dbError);
                }
                
                clearTimeout(timeoutId);
                updateState({ isGenerating: false }, false);
                resolve({ projectId, downloadUrl: result.s3Url });
                
              } catch (uploadError) {
                console.error('❌ S3 upload failed, falling back to data URL:', uploadError);
                
                // Fallback to data URL if S3 upload fails
                const downloadUrl = canvas.toDataURL('image/png');
                console.log('Canvas generation completed with data URL fallback, length:', downloadUrl.length);
                
                clearTimeout(timeoutId);
                updateState({ isGenerating: false }, false);
                resolve({ projectId, downloadUrl });
              }
            }, 'image/png', 0.95); // High quality PNG
            
          } catch (error) {
            console.error('Error during canvas generation:', error);
            clearTimeout(timeoutId);
            updateState({ isGenerating: false }, false);
            reject(error);
          }
        };
        
        // Start processing the image
        processImage().catch((error) => {
          console.error('Error in processImage:', error);
          clearTimeout(timeoutId);
          updateState({ isGenerating: false }, false);
          reject(error);
        });
      });
      
    } catch (error) {
      console.error('Error in generateFinalThumbnailCanvas outer try block:', error);
      updateState({ isGenerating: false }, false);
      throw error;
    }
  }, [state, updateState]);

  // NEW: Backend FFmpeg-based high-quality thumbnail generation
  const generateFinalThumbnailFFmpeg = useCallback(async (
    projectId: string, 
    editorCanvasDimensions?: { width: number; height: number }, 
    targetResolution?: { width: number; height: number }
  ) => {
    if (!state.baseImage) {
      throw new Error('No base image available');
    }

    console.log('🚀 Starting backend FFmpeg-based thumbnail generation...');
    
    updateState({ isGenerating: true }, false);
    
    try {
      // Create composition data to send to backend
      const compositionData = {
        baseImageUrl: state.baseImage,
        imageAdjustments: state.imageAdjustments,
        textElements: state.textElements,
        shapeElements: state.shapeElements,
        assetElements: state.assetElements,
        targetResolution: targetResolution || { width: 1920, height: 1080 },
        canvasSettings: state.canvasSettings
      };

      console.log('📤 Composition data for backend:', compositionData);

      // Use the existing canvas generation to create the base image
      const canvasResult = await generateFinalThumbnailCanvas(projectId, editorCanvasDimensions, targetResolution);
      
      // Convert the data URL to a blob
      const response = await fetch(canvasResult.downloadUrl);
      const blob = await response.blob();
      
      // Create form data for upload-composed endpoint
      const formData = new FormData();
      formData.append('thumbnail', blob, 'thumbnail.png');
      formData.append('projectId', projectId);
      formData.append('composition', JSON.stringify(compositionData));

      // Get authentication token
      const token = localStorage.getItem('authToken');
      const headers: Record<string, string> = {};
      
      // Add authorization header if token exists
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      console.log('📤 Sending to backend upload-composed endpoint...');

      // Upload to backend with FFmpeg processing
      const uploadResponse = await fetch(getApiUrl('/thumbnail/upload-composed'), {
        method: 'POST',
        headers,
        body: formData
      });

      if (!uploadResponse.ok) {
        const errorData = await uploadResponse.json().catch(() => ({}));
        throw new Error(errorData.message || `Backend upload failed: ${uploadResponse.status}`);
      }

      const result = await uploadResponse.json();
      
      console.log('✅ Backend FFmpeg thumbnail generation completed successfully!');
      console.log('📊 Result:', result);
      
      return {
        projectId,
        downloadUrl: result.data.s3Url
      };
      
    } catch (error) {
      console.error('❌ Backend FFmpeg thumbnail generation failed:', error);
      throw new Error(`Backend FFmpeg processing failed: ${error.message}`);
    } finally {
      updateState({ isGenerating: false }, false);
    }
  }, [state, generateFinalThumbnailCanvas, updateState]);

  const saveThumbnailProgress = useCallback(async (projectId: string) => {
    // Save current editing state - could be extended to save to backend
    // For now, this saves the current work-in-progress state
    console.log('🎯 [SAVE] Saving thumbnail progress for project:', projectId);
    console.log('🖼️ [SAVE] Current state baseImage:', state.baseImage);
    console.log('📊 [SAVE] Current state overview:', {
      hasBaseImage: !!state.baseImage,
      textElementsCount: state.textElements.length,
      shapeElementsCount: state.shapeElements.length,
      assetElementsCount: state.assetElements.length
    });
    
    try {
      // If there's a composed thumbnail, save it to the project
      if (state.baseImage) {
        console.log('✅ [SAVE] Found baseImage, updating project thumbnail field');
        
        // Import the updateProject function
        const { updateProject } = await import('../../../services/projectService');
        
        // Update the project with the thumbnail URL
        const updateData = {
          thumbnail: state.baseImage // Also update thumbnail field for compatibility
        };
        
        console.log('🔄 [SAVE] Calling updateProject with data:', updateData);
        
        const updatedProject = await updateProject(projectId, updateData);
        console.log('🎉 [SAVE] Project updated successfully:', updatedProject);
        console.log('🖼️ [SAVE] New thumbnail URL in project:', updatedProject.thumbnail);
        
      } else {
        console.log('⚠️ [SAVE] No baseImage found, only saving to localStorage');
      }
      
      // In a real implementation, you might want to save the current state
      // to backend as a draft or work-in-progress version
      const editorState = {
        baseImage: state.baseImage,
        originalBaseImage: state.originalBaseImage,
        imageAdjustments: state.imageAdjustments,
        textElements: state.textElements,
        shapeElements: state.shapeElements,
        assetElements: state.assetElements,
        canvasSettings: state.canvasSettings
      };
      
      // Save to localStorage as backup
      localStorage.setItem(`thumbnail_draft_${projectId}`, JSON.stringify(editorState));
      console.log('💾 [SAVE] Saved to localStorage as backup');
      
      return { success: true, message: 'Progress saved successfully' };
    } catch (error) {
      console.error('❌ [SAVE] Error saving progress:', error);
      throw error;
    }
  }, [state]);

  const loadThumbnailProgress = useCallback(async (projectId: string) => {
    try {
      // Try to load saved progress from localStorage
      const savedState = localStorage.getItem(`thumbnail_draft_${projectId}`);
      if (savedState) {
        const editorState = JSON.parse(savedState);
        updateState({
          baseImage: editorState.baseImage || '',
          originalBaseImage: editorState.originalBaseImage || editorState.baseImage || '',
          imageAdjustments: editorState.imageAdjustments || initialState.imageAdjustments,
          textElements: editorState.textElements || [],
          shapeElements: editorState.shapeElements || [],
          assetElements: editorState.assetElements || [],
          canvasSettings: editorState.canvasSettings || initialState.canvasSettings
        }, false);
        return { success: true, message: 'Progress loaded successfully' };
      }
      return { success: false, message: 'No saved progress found' };
    } catch (error) {
      console.error('Error loading progress:', error);
      throw error;
    }
  }, [updateState]);

  return {
    // State
    ...state,
    
    // Actions
    loadProject,
    updateImageAdjustments,
    addTextElement,
    updateTextElement,
    removeTextElement,
    addShapeElement,
    updateShapeElement,
    removeShapeElement,
    addAssetElement,
    updateAssetElement,
    removeAssetElement,
    selectElement,
    generateFinalThumbnail,
    generateFinalThumbnailCanvas, // Canvas-based method
    generateFinalThumbnailFFmpeg, // NEW: Backend FFmpeg-based high-quality method
    saveThumbnailProgress,
    loadThumbnailProgress,
    
    // History
    undo,
    redo,
    canUndo: state.historyIndex > 0,
    canRedo: state.historyIndex < state.history.length - 1
  };
};